import { createAsyncThunk } from '@reduxjs/toolkit';
import { QueryClient } from 'react-query';
import {
  CaseByIdQuery,
  useAddCaseCategoryMutation,
  useRemoveCaseCategoryMutation,
} from 'src/graphql';
import { TRootState } from 'src/store';
import { sortObjectByName } from 'src/utils/sortObjectByName';
import { caseSlice } from '../Case.slice';

type CaseCategoryUpdatedArgs = {
  caseId: string;
  newSelectedCategories: { id: number; name: string }[];
  mutationAddCaseCategory: ReturnType<
    typeof useAddCaseCategoryMutation
  >['mutateAsync'];
  mutationRemoveCaseCategory: ReturnType<
    typeof useRemoveCaseCategoryMutation
  >['mutateAsync'];
  queryClient: QueryClient;
};

export const caseCategoryUpdated = createAsyncThunk<
  void,
  CaseCategoryUpdatedArgs,
  { state: TRootState }
>(
  'case/categoryUpdated',
  async (
    {
      caseId,
      newSelectedCategories,
      mutationAddCaseCategory,
      mutationRemoveCaseCategory,
      queryClient,
    }: CaseCategoryUpdatedArgs,
    thunkAPI,
  ) => {
    const state = thunkAPI.getState();
    const selectedCategoriesIds = caseSlice
      .getSelectors()
      .getSelectedCategories(state.case)
      .map((category) => category.id);
    const newSelectedCategoriesIds = newSelectedCategories.map(
      (category) => category.id,
    );

    const activeCategories = state.categories.activeCategories || [];

    const categoryIdToBeAdded = newSelectedCategoriesIds.filter(
      (category) => !selectedCategoriesIds.includes(category),
    )?.[0];
    const categoryIdToBeRemoved = selectedCategoriesIds.filter(
      (category) => !newSelectedCategoriesIds.includes(category),
    )?.[0];

    const updateCacheWithAddedCategory = (args: {
      categoryId: number;
      caseId: string;
    }) => {
      const category = activeCategories.find(
        (activeCategory) => activeCategory.id === args.categoryId,
      );

      queryClient.cancelQueries(['CaseById', { id: caseId }]);
      queryClient.setQueryData<CaseByIdQuery | undefined>(
        ['CaseById', { id: caseId }],
        (oldData) => {
          if (!oldData?.caseById?.Categories || !category) {
            return oldData;
          }

          return {
            ...oldData,
            caseById: {
              ...oldData.caseById,
              Categories: [...oldData.caseById.Categories, { ...category }],
            },
          };
        },
      );
    };

    const updateCacheWithRemovedCategory = (args: {
      categoryId: number;
      caseId: string;
    }) => {
      queryClient.setQueryData<CaseByIdQuery | undefined>(
        ['CaseById', { id: caseId }],
        (oldData) => {
          if (!oldData?.caseById?.Categories) {
            return oldData;
          }
          return {
            ...oldData,
            caseById: {
              ...oldData.caseById,
              Categories: oldData.caseById.Categories.filter(
                (oldCategory) => oldCategory.id !== args.categoryId,
              ).sort(sortObjectByName),
            },
          };
        },
      );
    };

    if (categoryIdToBeAdded) {
      try {
        updateCacheWithAddedCategory({
          categoryId: categoryIdToBeAdded,
          caseId: caseId,
        });

        await mutationAddCaseCategory({
          caseId,
          categoryId: categoryIdToBeAdded,
        });

        queryClient.invalidateQueries(['CaseById', { id: caseId }]);
        queryClient.invalidateQueries([
          'GetCaseAuditEntries',
          { case: caseId },
        ]);
      } catch (e) {
        updateCacheWithRemovedCategory({
          categoryId: categoryIdToBeAdded,
          caseId: caseId,
        });

        throw e;
      }
    } else if (categoryIdToBeRemoved) {
      try {
        queryClient.cancelQueries(['CaseById', { id: caseId }]);
        updateCacheWithRemovedCategory({
          categoryId: categoryIdToBeRemoved,
          caseId: caseId,
        });

        await mutationRemoveCaseCategory({
          caseId,
          categoryId: categoryIdToBeRemoved,
        });

        queryClient.invalidateQueries(['CaseById', { id: caseId }]);
        queryClient.invalidateQueries([
          'GetCaseAuditEntries',
          { case: caseId },
        ]);
      } catch (e) {
        updateCacheWithAddedCategory({
          categoryId: categoryIdToBeRemoved,
          caseId: caseId,
        });

        throw e;
      }
    }
  },
);
